// src/views/admin/AdminLayout.jsx
import { useState, useEffect } from "react";
import { Routes, Route, NavLink, useNavigate, useLocation, Navigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { usePermissions } from "../../context/PermissionsContext";
import logo from "../../assets/images/logo.png";

// Pages Admin
import ADAccueil from "./ADAccueil";
import ADDashboard from "./ADDashboard";
import ADUtilisateurs from "./ADUtilisateurs";
import ADAnneesScolaires from "./ADAnneesScolaires";
import ADClasses from "./ADClasses";
import ADEleves from "./ADEleves";

import ADInscriptionNouvelEleve from "./ADInscriptionNouvelEleve";
import ADInscriptionReinscription from "./ADInscriptionReinscription";

import ADMatieres from "./ADMatieres";
import ADTrimestres from "./ADTrimestres";
import ADPlannifications from "./ADPlannifications";
import ADNotes from "./ADNotes";
import ADBulletins from "./ADBulletins";
import ADConfigBulletins from "./ADConfigBulletins";
import ADConfigCartes from "./ADConfigCartes";
import ADCartes from "./ADCartes";
import ADPointage from "./ADPointage";
import ADMessages from "./ADMessages";
import ADNotifications from "./ADNotifications";
import ADParents from "./ADParents";
import ADParametres from "./ADParametres";
import ADImportEleves from "./ADImportEleves";
import ADImportUtilisateurs from "./ADImportUtilisateurs";
import ADEtablissement from "./ADEtablissement";
import ADImportNotesExcel from "./ADImportNotesExcel";
import ADAbonnements from "./ADAbonnements";
import ADParametresEleves from "./ADParametresEleves";
import ADMonCompte from "./ADMonCompte";

import ADCaisse from "./ADCaisse";

// Code de permission requis pour chaque entrée de nav. "ACCUEIL" reste
// toujours visible (page d'atterrissage après connexion) — toutes les
// autres entrées, y compris DASHBOARD, sont soumises à canSee().
const NAV_PERMISSIONS = {
  dashboard: "dashboard.voir",
  classes: "classes.voir",
  eleves: "eleves.voir",
  utilisateurs: "utilisateurs.voir",
  messages: "messagerie.voir",
  notifications: "notifications.voir",
  pointage: "pointage.voir",
  abonnements: "abonnements.voir",
  caisse: "caisse.voir",
  parametres: "parametres.voir",
};

const PAGE_TITLES = {
  "/admin/accueil": "Accueil",
  "/admin/dashboard": "Vue d'ensemble",
  "/admin/utilisateurs": "Gestion des utilisateurs",
  "/admin/annees-scolaires": "Années scolaires",
  "/admin/classes": "Classes",
  "/admin/eleves": "Élèves",

  "/admin/eleves/nouveau": "Nouvelle inscription d'un élève",
  "/admin/eleves/inscrire": "Inscrire / réinscrire un élève",

  "/admin/matieres": "Matières",
  "/admin/trimestres": "Trimestres",
  "/admin/plannifications": "Plannifications",
  "/admin/notes": "Saisie des notes",
  "/admin/bulletins": "Bulletins",
  "/admin/config-bulletins": "Configuration des bulletins",
  "/admin/config-cartes": "Configuration des cartes scolaires",
  "/admin/cartes": "Génération des cartes scolaires",
  "/admin/pointage": "Pointage",
  "/admin/messages": "Messagerie",
  "/admin/notifications": "Notifications",
  "/admin/parents": "Parents",
  "/admin/parametres": "Paramètres",
  "/admin/import-eleves": "Import des élèves depuis Excel",
  "/admin/abonnements": "Abonnements",
  "/admin/parametres-eleves": "Paramètres des élèves",

  "/admin/caisse": "Caisse",
  "/admin/mon-compte": "Mon compte",
};

const PAGE_ICONS = {
  "/admin/accueil": "ti-home",
  "/admin/dashboard": "ti-layout-dashboard",
  "/admin/utilisateurs": "ti-user-cog",
  "/admin/classes": "ti-school",
  "/admin/eleves": "ti-users",

  "/admin/eleves/nouveau": "ti-user-plus",
  "/admin/eleves/inscrire": "ti-file-check",

  "/admin/parents": "ti-user-check",
  "/admin/messages": "ti-messages",
  "/admin/notifications": "ti-bell",
  "/admin/pointage": "ti-calendar-check",
  "/admin/parametres": "ti-settings",
  "/admin/parametres-eleves": "ti-settings-2",
  "/admin/caisse": "ti-cash",
  "/admin/mon-compte": "ti-user-edit",
};

const S = {
  header: {
    display: "flex", alignItems: "center", gap: 16,
  },
  h1: {
    margin: 0, fontSize: 22, fontWeight: 700,
    color: "var(--pr)", textTransform: "uppercase", letterSpacing: 1,
    fontFamily: "'Cinzel', 'Times New Roman', serif",
  },
};

function NavItem({ to, icon, label }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => `ni${isActive ? " active" : ""}`}
      aria-current={({ isActive }) => (isActive ? "page" : undefined)}
      title={label}
    >
      <i className={`ti ${icon}`} aria-hidden="true" />
      <span className="ni-label">{label}</span>
    </NavLink>
  );
}

// Clé localStorage pour mémoriser l'état replié/déplié entre sessions
const SB_COLLAPSE_KEY = "kamalpha_sb_collapsed";

export default function AdminLayout() {
  const { user, logoutUser } = useAuth();
  const { canSee, loadingPermissions } = usePermissions();
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SB_COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SB_COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        // stockage indisponible (mode privé, etc.) : on ignore, l'état reste en mémoire
      }
      return next;
    });
  };

  // On reconstruit la clé à partir de TOUT ce qui suit "/admin/" (et pas
  // seulement le dernier segment), sinon les routes imbriquées comme
  // "eleves/nouveau" ou "eleves/inscrire" ne matchent jamais PAGE_TITLES
  // et retombent sur "Administration".
  const getPageKey = () => {
    const idx = location.pathname.indexOf("/admin/");
    if (idx === -1) return "accueil";
    let rest = location.pathname.slice(idx + "/admin/".length).replace(/\/$/, "");
    if (!rest) return "accueil";
    // Cas particulier : "eleves/:eleveId/inscrire" a un id dynamique entre
    // les deux segments fixes → on le retire pour retomber sur la même clé
    // que "eleves/inscrire".
    const parts = rest.split("/");
    if (parts[0] === "eleves" && parts.length === 3 && parts[2] === "inscrire") {
      rest = "eleves/inscrire";
    }
    return rest;
  };

  const page = getPageKey();
  const pageTitle = PAGE_TITLES[`/admin/${page}`] ?? "Administration";
  const pageIcon = PAGE_ICONS[`/admin/${page}`] ?? "ti-layout-dashboard";
  const isAccueil = page === "accueil";

  const initials = user?.username
    ? user.username.charAt(0).toUpperCase()
    : "A";

  // Nom complet affiché sur la page d'accueil, avec repli en cascade
  const nomAdmin =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
    user?.username ||
    "";

  const getAdminPath = (path) => {
    if (!slug) return "/login";
    return `/${slug}/admin/${path}`;
  };

  if (!slug) {
    return (
      <div className="loader-container">
        <div style={{ textAlign: "center", color: "var(--m)" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🏫</div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Chargement...</div>
          <div style={{ fontSize: 14 }}>Aucun établissement trouvé</div>
        </div>
      </div>
    );
  }

  // ⚠️ Correctif : tant que les permissions ne sont pas connues, on
  // n'affiche NI le menu NI le contenu. C'est ce qui supprime le flash
  // d'onglets non autorisés visible sur connexion lente.
  if (loadingPermissions) {
    return (
      <div className="loader-container">
        <div className="loader-spinner" />
      </div>
    );
  }

  return (
    <>
      <style>{CSS}</style>
      <div className={`app${collapsed ? " sb-collapsed" : ""}`}>
        <aside className="sb" role="complementary">
          <button
            type="button"
            className="sb-toggle"
            onClick={toggleCollapsed}
            aria-label={collapsed ? "Déployer le menu" : "Réduire le menu"}
            title={collapsed ? "Déployer le menu" : "Réduire le menu"}
          >
            <i className={`ti ${collapsed ? "ti-chevron-right" : "ti-chevron-left"}`} aria-hidden="true" />
          </button>

          {/* Logo + Nom de l'application */}
          <div className="sbrand">
            <div className="sbrand-logo-wrapper">
              <img src={logo} alt="Logo" className="sblogo" />
            </div>
            <div className="sbrand-name">
              <span className="sbrand-title">Suivi Scolaire</span>
              <span className="sbrand-subtitle">Espace Administration</span>
            </div>
          </div>

          <nav className="snav" role="navigation" aria-label="Navigation principale">
            <ul className="nav flex-column">
              <li className="nav-item">
                <NavItem to={getAdminPath("accueil")} icon="ti-home" label="ACCUEIL" />
              </li>

              {canSee(NAV_PERMISSIONS.dashboard) && (
                <li className="nav-item">
                  <NavItem to={getAdminPath("dashboard")} icon="ti-layout-dashboard" label="DASHBOARD" />
                </li>
              )}

              {canSee(NAV_PERMISSIONS.classes) && (
                <li className="nav-item">
                  <NavItem
                    to={getAdminPath("classes")}
                    icon="ti-school"
                    label="GESTION PÉDAGOGIQUE"
                  />
                </li>
              )}

              {canSee(NAV_PERMISSIONS.eleves) && (
                <li className="nav-item">
                  <NavItem to={getAdminPath("eleves")} icon="ti-users" label="ÉLÈVES" />
                </li>
              )}

              {canSee(NAV_PERMISSIONS.utilisateurs) && (
                <li className="nav-item">
                  <NavItem to={getAdminPath("utilisateurs")} icon="ti-user-cog" label="UTILISATEURS" />
                </li>
              )}

              {/* <li className="nav-item">
                <NavItem to={getAdminPath("parents")} icon="ti-user-check" label="PARENTS" />
              </li> */}

              {canSee(NAV_PERMISSIONS.messages) && (
                <li className="nav-item">
                  <NavItem to={getAdminPath("messages")} icon="ti-messages" label="MESSAGE" />
                </li>
              )}

              {canSee(NAV_PERMISSIONS.notifications) && (
                <li className="nav-item">
                  <NavItem to={getAdminPath("notifications")} icon="ti-bell" label="NOTIFICATION" />
                </li>
              )}

              {canSee(NAV_PERMISSIONS.pointage) && (
                <li className="nav-item">
                  <NavItem to={getAdminPath("pointage")} icon="ti-calendar-check" label="PRÉSENCE" />
                </li>
              )}

              {canSee(NAV_PERMISSIONS.abonnements) && (
                <li className="nav-item">
                  <NavItem to={getAdminPath("abonnements")} icon="ti-crown" label="ABONNEMENTS" />
                </li>
              )}

              {canSee(NAV_PERMISSIONS.caisse) && (
                <li className="nav-item">
                  <NavItem to={getAdminPath("caisse")} icon="ti-cash" label="CAISSE" />
                </li>
              )}

              {canSee(NAV_PERMISSIONS.parametres) && (
                <li className="nav-item">
                  <NavItem to={getAdminPath("parametres")} icon="ti-settings" label="CONFIGURATION" />
                </li>
              )}
            </ul>
          </nav>

          {/* Bloc utilisateur */}
          {user && (
            <div className="susr">
              <div className="uav" title={user.username}>{initials}</div>
              <p className="unm" title={user.username}>{user.username}</p>
              <div className="susr-actions">
                <NavLink to={getAdminPath("mon-compte")} className="susr-btn susr-edit" title="Modifier le profil">
                  <i className="ti ti-user-edit" aria-hidden="true" />
                  <span className="susr-btn-label">Modifier le profil</span>
                </NavLink>
                <button onClick={() => logoutUser()} className="susr-btn susr-logout" title="Déconnexion">
                  <i className="ti ti-logout" aria-hidden="true" />
                  <span className="susr-btn-label">Déconnexion</span>
                </button>
              </div>
            </div>
          )}
        </aside>

        <main className="main">
          {!isAccueil && (
            <div className="topbar">
              <div style={S.header}>
                <i className={`ti ${pageIcon}`} style={{ color: "var(--or)", fontSize: 28 }} aria-hidden="true" />
                <h1 style={S.h1}>{pageTitle}</h1>
              </div>
              <div className="tbr">
                <button className="ibtn" onClick={() => window.location.reload()}>
                  <i className="ti ti-refresh" aria-hidden="true" />
                </button>
              </div>
            </div>
          )}

          <div className={isAccueil ? "content content-full" : "content"}>
            <Routes>
              <Route index element={<Navigate to="accueil" replace />} />
              <Route path="accueil" element={<ADAccueil slug={slug} adminNom={nomAdmin} />} />
              <Route path="dashboard" element={<ADDashboard slug={slug} />} />
              <Route path="utilisateurs" element={<ADUtilisateurs slug={slug} />} />
              <Route path="annees-scolaires" element={<ADAnneesScolaires slug={slug} />} />
              <Route path="classes" element={<ADClasses slug={slug} />} />
              <Route path="eleves" element={<ADEleves slug={slug} />} />
              <Route path="matieres" element={<ADMatieres slug={slug} />} />
              <Route path="trimestres" element={<ADTrimestres slug={slug} />} />
              <Route path="plannifications" element={<ADPlannifications slug={slug} />} />
              <Route path="notes" element={<ADNotes slug={slug} />} />
              <Route path="bulletins" element={<ADBulletins slug={slug} />} />
              <Route path="config-bulletins" element={<ADConfigBulletins slug={slug} />} />
              <Route path="config-cartes" element={<ADConfigCartes slug={slug} />} />
              <Route path="cartes" element={<ADCartes slug={slug} />} />
              <Route path="pointage" element={<ADPointage slug={slug} />} />
              <Route path="abonnements" element={<ADAbonnements slug={slug} />} />

              <Route path="messages" element={<ADMessages slug={slug} />} />
              <Route path="notifications" element={<ADNotifications slug={slug} />} />
              <Route path="parents" element={<ADParents slug={slug} />} />
              <Route path="parametres" element={<ADParametres slug={slug} />} />
              <Route path="import-eleves" element={<ADImportEleves slug={slug} />} />
              <Route path="import-utilisateurs" element={<ADImportUtilisateurs slug={slug} />} />
              <Route path="etablissement" element={<ADEtablissement slug={slug} />} />
              <Route path="import-notes-excel" element={<ADImportNotesExcel slug={slug} />} />

              <Route path="mon-compte" element={<ADMonCompte slug={slug} />} />

              <Route path="parametres-eleves" element={<ADParametresEleves slug={slug} />} />

              <Route path="caisse" element={<ADCaisse slug={slug} />} />

              <Route path="eleves/nouveau" element={<ADInscriptionNouvelEleve slug={slug} />} />
              <Route path="eleves/inscrire" element={<ADInscriptionReinscription slug={slug} />} />
              <Route path="eleves/:eleveId/inscrire" element={<ADInscriptionReinscription slug={slug} />} />
            </Routes>
          </div>
        </main>
      </div>
    </>
  );
}

// ─── CSS ──────────────────────────────────────────────────────────────
// Thème Kamalpha : bleu profond + or du logo.
// Les @import doivent rester tout en haut de la chaîne.
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Cinzel:wght@600;700&display=swap');
@import url('https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/dist/tabler-icons.min.css');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  /* Bleu du logo */
  --pr:    #1B4F80;
  --pr-lt: #2F6DA8;
  --pr-dk: #0E2F52;
  --pr-bg: #E6EEF7;

  /* Or du logo (identité uniquement : jamais pour signaler un statut) */
  --or:    #C08A2E;
  --or-lt: #E3B75A;   /* or lisible sur fond sombre */
  --or-dk: #8A5A14;   /* or utilisable pour du texte sur fond clair */

  --bl: #1a6fff; --bl-bg: #e8f1ff; --bl-dk: #0c447c;
  --tl: #0f6e56; --tl-bg: #e1f5ee;
  --am: #ba7517; --am-bg: #faeeda;
  --rd: #dc3545; --rd-dk: #b02a37; --rd-bg: #fcebeb;
  --gn: #3b6d11; --gn-bg: #eaf3de;
  --s:  #ffffff;
  --s2: #f5f6fa;
  --b:  #e8e8ee;
  --b2: #d0d0da;
  --t:  #0f1b2d;
  --m:  #64748b;
  --h:  #9ba5b4;
  --radius-lg: 10px;
  --radius-md: 7px;
}

body {
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background: var(--s2);
  font-size: 16px;
  color: var(--t);
  margin: 0;
}

.app  { display: flex; min-height: 100vh; }

.sb {
  width: 250px;
  flex-shrink: 0;
  background: linear-gradient(180deg, var(--pr-dk) 0%, var(--pr) 100%);
  border-right: 1px solid rgba(192, 138, 46, 0.35);
  box-shadow: 2px 0 8px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  position: fixed; top: 0; left: 0; height: 100vh;
  z-index: 100;
  overflow-y: auto;
  overflow-x: hidden;
  color: white;
  transition: width .25s ease;
}
.main {
  flex: 1;
  margin-left: 250px;
  display: flex;
  flex-direction: column;
  min-width: 0;
  transition: margin-left .25s ease;
}

.sb-toggle {
  position: absolute;
  top: 24px;
  right: -14px;
  width: 30px; height: 30px;
  border-radius: 50%;
  border: 1.5px solid var(--or);
  background: var(--s);
  color: var(--pr);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  font-size: 15px;
  z-index: 101;
  box-shadow: 0 2px 6px rgba(0,0,0,0.25);
  transition: background-color .12s, transform .12s, box-shadow .12s;
}
.sb-toggle:hover {
  background: var(--or);
  color: white;
  transform: scale(1.08);
  box-shadow: 0 3px 8px rgba(0,0,0,0.3);
}
.sb-toggle i { font-size: 16px; }

.sbrand {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 30px;
  margin-top: 24px;
  padding: 0 15px;
  flex-shrink: 0;
}

.sbrand-logo-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.sblogo {
  width: 130px;
  height: auto;
  padding: 6px;
  background-color: white;
  border: 1px solid rgba(192, 138, 46, 0.5);
  border-radius: 10px;
  display: block;
  transition: width .25s ease;
}

.sbrand-name {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  margin-top: 14px;
  width: 100%;
}

.sbrand-title {
  font-size: 17px;
  font-weight: 700;
  color: white;
  letter-spacing: 0.5px;
  line-height: 1.2;
  text-align: center;
}

.sbrand-subtitle {
  font-size: 11px;
  font-weight: 400;
  color: var(--or-lt);
  text-transform: uppercase;
  letter-spacing: 0.8px;
  text-align: center;
}

.snav { padding: 0 12px; flex-shrink: 0; }
.snav ul { list-style: none; }
.nav-item { list-style: none; }

.ni {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 15px; border-radius: 5px; cursor: pointer;
  font-size: 16px; font-weight: 400; color: white; background: none;
  border: none; width: 100%; text-align: left;
  transition: all 0.3s;
  font-family: inherit;
  text-decoration: none; margin-bottom: 2px;
  white-space: nowrap;
}
.ni:hover { background: rgba(255, 255, 255, 0.10); }
.ni.active {
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-weight: 600;
  box-shadow: inset 3px 0 0 var(--or);
}
.ni i { font-size: 20px; width: 20px; flex-shrink: 0; }
.ni.active i { color: var(--or-lt); }
.ni-label { overflow: hidden; text-overflow: ellipsis; }

.susr {
  flex-shrink: 0;
  padding: 20px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: white;
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  margin: 24px 12px 12px;
  transition: padding .25s ease;
}
.uav {
  width: 64px; height: 64px; border-radius: 50%;
  background-color: var(--pr-dk);
  border: 2px solid var(--or);
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 24px;
  user-select: none; text-transform: uppercase;
  flex-shrink: 0;
}
.unm {
  margin: 0; font-weight: 600; font-size: 1.1rem;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 180px;
}

.susr-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.susr-btn {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: none;
  border-radius: 8px;
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}

.susr-btn i {
  font-size: 16px;
  flex-shrink: 0;
}

.susr-btn-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.susr-edit {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
}
.susr-edit:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.susr-logout {
  background-color: var(--rd);
  color: white;
}
.susr-logout:hover {
  background-color: var(--rd-dk);
}

.ibtn { width: 30px; height: 30px; border-radius: 5px; border: 0.5px solid var(--b); background: var(--s); display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--m); font-size: 17px; transition: all .12s; }
.ibtn:hover { background: var(--s2); color: var(--t); }

.topbar  {
  padding: 14px 20px;
  display: flex; align-items: center; justify-content: space-between;
  background: var(--s); flex-shrink: 0; position: sticky; top: 0; z-index: 50;
  border-bottom: none;
}
.topbar::after {
  content: "";
  position: absolute; left: 0; right: 0; bottom: 0; height: 3px;
  background: linear-gradient(90deg, var(--pr) 0%, var(--pr-lt) 55%, var(--or) 100%);
}
.tbr     { display: flex; align-items: center; gap: 5px; }

.content { flex: 1; overflow-y: auto; padding: 16px 18px; }

.bprim { display: flex; align-items: center; gap: 5px; padding: 8px 15px; background: var(--pr); color: #fff; border: none; border-radius: 5px; font-size: 15px; cursor: pointer; font-family: inherit; font-weight: 500; white-space: nowrap; transition: opacity .12s; }
.bprim:hover { opacity: .88; }
.bprim.rd { background: var(--rd); }
.bsec  { display: flex; align-items: center; gap: 5px; padding: 8px 13px; background: var(--s); color: var(--m); border: 0.5px solid var(--b2); border-radius: 5px; font-size: 15px; cursor: pointer; font-family: inherit; transition: all .12s; }
.bsec:hover { background: var(--s2); color: var(--t); }

.pill     { display: inline-block; padding: 4px 9px; border-radius: 9px; font-size: 13px; font-weight: 500; }
.pill.ok  { background: var(--gn-bg); color: var(--gn); }
.pill.warn{ background: var(--am-bg); color: var(--am); }
.pill.err { background: var(--rd-bg); color: var(--rd); }
.pill.info{ background: var(--bl-bg); color: var(--bl-dk); }
.pill.pu  { background: var(--pr-bg); color: var(--pr); }
.pill.neu { background: var(--s2); color: var(--m); }

.card  { background: var(--s); border: 0.5px solid var(--b); border-radius: var(--radius-md); padding: 14px; }
.ch    { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.ct    { font-size: 16px; font-weight: 600; color: var(--t); }

.sg    { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 9px; margin-bottom: 14px; }
.sc    { background: var(--s2); border-radius: var(--radius-md); padding: 11px; }
.sclbl { font-size: 14px; color: var(--m); margin-bottom: 4px; display: flex; align-items: center; gap: 3px; }
.sclbl i { font-size: 15px; }
.scval { font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; font-size: 28px; font-weight: 600; color: var(--t); line-height: 1; }
.sc-accent { border-left: 3px solid var(--pr); }
.sc-accent-bl { border-left: 3px solid var(--bl); }
.sc-accent-gn { border-left: 3px solid var(--gn); }
.sc-accent-am { border-left: 3px solid var(--am); }

.tbl   { width: 100%; border-collapse: collapse; font-size: 15px; table-layout: fixed; }
.tbl th { text-align: left; padding: 9px 10px; color: var(--m); font-weight: 600; border-bottom: 0.5px solid var(--b); font-size: 14px; white-space: nowrap; }
.tbl td { padding: 10px 10px; border-bottom: 0.5px solid var(--b); color: var(--t); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tbl tr:last-child td { border-bottom: none; }
.tbl tr:hover td { background: var(--s2); }

.act { display: flex; gap: 3px; }
.act button { width: 26px; height: 26px; border-radius: 4px; border: 0.5px solid var(--b); background: none; cursor: pointer; color: var(--m); display: flex; align-items: center; justify-content: center; font-size: 15px; transition: all .12s; }
.act button:hover     { background: var(--s2); color: var(--t); border-color: var(--b2); }
.act button.del:hover { background: var(--rd-bg); color: var(--rd); border-color: var(--rd); }
.act button.ok:hover  { background: var(--gn-bg); color: var(--gn); border-color: var(--gn); }
.act button.warn:hover{ background: var(--am-bg); color: var(--am); border-color: var(--am); }

.av  { width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; flex-shrink: 0; }
.av.bl { background: var(--bl-bg); color: var(--bl-dk); }
.av.tl { background: var(--tl-bg); color: var(--tl); }
.av.pu { background: var(--pr-bg); color: var(--pr); }
.av.am { background: var(--am-bg); color: var(--am); }
.avc { display: flex; align-items: center; gap: 5px; }

.sb2 { display: flex; align-items: center; gap: 6px; margin-bottom: 11px; flex-wrap: wrap; }
.sb2 input  { flex: 1; min-width: 120px; padding: 8px 13px; border: 0.5px solid var(--b2); border-radius: 5px; font-size: 15px; background: var(--s); color: var(--t); font-family: inherit; }
.sb2 select { padding: 8px 13px; border: 0.5px solid var(--b2); border-radius: 5px; font-size: 15px; background: var(--s); color: var(--t); font-family: inherit; }

.row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 11px; margin-bottom: 12px; }

.fg          { display: flex; flex-direction: column; gap: 3px; }
.fg label    { font-size: 14px; color: var(--m); font-weight: 500; }
.fg input, .fg select, .fg textarea { padding: 9px 13px; border: 0.5px solid var(--b2); border-radius: 5px; font-size: 15px; background: var(--s); color: var(--t); font-family: inherit; width: 100%; }
.fg textarea { resize: vertical; min-height: 56px; }
.fgrid       { display: grid; grid-template-columns: 1fr 1fr; gap: 9px; }

.factions { display: flex; gap: 7px; margin-top: 12px; justify-content: flex-end; }

.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.35); z-index: 200; display: flex; align-items: center; justify-content: center; padding: 16px; }
.modal-box     { background: var(--s); border-radius: var(--radius-lg); border: 0.5px solid var(--b); padding: 18px; width: 450px; max-width: 100%; max-height: 90vh; overflow-y: auto; }
.modal-title   { font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; font-size: 17px; font-weight: 600; color: var(--t); margin-bottom: 13px; display: flex; align-items: center; justify-content: space-between; }

.loader-container { display: flex; justify-content: center; align-items: center; min-height: 300px; width: 100%; }
.loader-spinner { width: 48px; height: 48px; border: 4px solid var(--s2); border-top: 4px solid var(--pr); border-radius: 50%; animation: spin 0.8s linear infinite; }

@keyframes spin {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.content-full {
  padding: 0;
  overflow: hidden;
}

.app.sb-collapsed .sb { width: 76px; }
.app.sb-collapsed .main { margin-left: 76px; }

.app.sb-collapsed .sbrand-name,
.app.sb-collapsed .ni-label,
.app.sb-collapsed .susr-btn-label,
.app.sb-collapsed .unm {
  display: none;
}

.app.sb-collapsed .sbrand { padding: 0 8px; }
.app.sb-collapsed .sblogo { width: 40px; }
.app.sb-collapsed .ni { justify-content: center; padding: 10px 0; }
.app.sb-collapsed .susr { padding: 12px 8px; }
.app.sb-collapsed .susr-btn { padding: 0; }
.app.sb-collapsed .sb-toggle i { transform: none; }

::-webkit-scrollbar { width: 4px; }
::-webkit-scrollbar-track { background: var(--s2); }
::-webkit-scrollbar-thumb { background: var(--b2); border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: var(--m); }
`;